import {
  UniversalContext,
  UserInfo,
  UserUpdateEvent,
  useUniversalContext,
} from "@cxc/react-universal-components";
import { AemPageResponses } from "clients/AEMTypes";
import React, { createContext, useEffect, useMemo, useState } from "react";
import { BaseErrorPage } from "../error/BaseErrorPage";
import { useCanadaPopUp } from "./UseCanadaPopUp";
import { useFetchedPages } from "./useFetchedPages";
import { useRankedPersonas } from "./useRankedPersonas";

type User = Partial<UserInfo>;

interface AppProviderProps {
  className?: string;
  children: React.ReactElement | React.ReactElement[];
  scope: string;
  aemBaseUrl: string;
  pagesToFetch: string[];
  authenticated: boolean;
}

interface AppProviderContextProps {
  locale: string;
  language: string;
  country: string;
  navigateToPage: (page: string) => void;
  user: User;
  pagesToProvide: AemPageResponses;
  pageLoadFailed: boolean;
}

const AppProviderContext = createContext<AppProviderContextProps>({
  locale: "You should not get this",
  language: "Not the language",
  country: "Not the country",
  navigateToPage: () => console.log("no"),
  user: {},
  pagesToProvide: {},
  pageLoadFailed: false,
});

const AppProvider = ({
  className,
  children,
  scope,
  aemBaseUrl,
  pagesToFetch,
  authenticated,
}: AppProviderProps) => {
  const {
    language: locale,
    navigate,
    user: initialUser,
  } = useUniversalContext() as UniversalContext;
  const [language, country] = locale.split("-");
  const [user, setUser] = useState(initialUser);

  const rankedPersonaObject = useRankedPersonas({
    scope,
    locale,
    authenticated,
    aemBaseUrl,
  });

  const { pagesToProvide, pageLoadFailed } = useFetchedPages({
    pagesToFetch,
    aemBaseUrl,
    rankedPersonaObject,
    scope,
    locale,
    authenticated,
  });

  const { CanadaPopUp } = useCanadaPopUp();

  useEffect(() => {
    const updateUser = (e: Event) => {
      const realEvent = e as UserUpdateEvent;
      setUser(realEvent.detail.user);
    };
    document.addEventListener("universal-context-update", updateUser);

    return () => {
      document.removeEventListener("universal-context-update", updateUser);
    };
  }, []);

  const value = useMemo(
    () => ({
      locale,
      language,
      country,
      navigateToPage: navigate,
      user,
      pagesToProvide,
      pageLoadFailed,
    }),
    [locale, language, country, navigate, user, pagesToProvide, pageLoadFailed],
  );
  return (
    <AppProviderContext.Provider value={value}>
      {CanadaPopUp}
      <div data-testid="app-provider" className={className}>
        {pageLoadFailed ? <BaseErrorPage /> : children}
      </div>
    </AppProviderContext.Provider>
  );
};

export { AppProviderContext, AppProvider as default };
export type { AppProviderContextProps, AppProviderProps };
